import React, {useState, useEffect, useRef, useMemo} from 'react'
import { Image, Space, PreviewGroup, Modal, Form, Tooltip } from '@bedrock/components'
import {ChevronRight, Play} from '@bedrock/icons-react'
import {useMemoizedFn} from 'ahooks'
import classNames from 'classnames/bind'
import styles from './index.less'
import { useOpenModal } from "#utils/openModal";
import DescriptionsFormItem from "../../../components/DescriptionsFormItem";
import EquityFormTable, { getExpectRightsLabel } from '#features/EquityFormTable'
import NoData from '@bedrock/components/es/Empty/images/small/NoData'
import { renderCooperationPropertyLabel, renderCooperationPropertyValue } from '#features/CooperationFormList/CooperationDetails'
import KPITable from "#features/KPITable";

const cx = classNames.bind(styles)

// 打开打开合作内容详情
function useOpenContentDetails(props) {
  const [state, setState] = useState({
    title: '',
    property_values: [],
    necessary_rights: [],
    expect_rights_diff_list: [],
    kpi_values: [],
    cooperate_account_id: undefined,
    remark: '',
  })

  const showList = useMemo(() => {
    return state.property_values.map((item, index) => {
      return {
        value: item.value?.join("、") || item.value || '--',
        ...item,
      }
    })
  }, [state.property_values]);

  const items = useMemo(() => {
    return [
      ...(showList?.map(item => {
        return {
          label: renderCooperationPropertyLabel(item),
          formItemKey: item?.property_code,
          view: true,
          render: () => {
            return <span>{renderCooperationPropertyValue(item)}</span>;
          }
        }
      }) || []),
      {
        label: '考核指标',
        view: true,
        formItemKey: 'kpi_values',
        span: 2,
        render: () => <KPITable
        structuredVersion={props?.detail?.structured_version}
        view
        required={false}
        platforms={['MPC']}
        emptyRender={() => {
            return <div>--</div>
        }}
        cooperate_account_id={state?.cooperate_account_id}
    />
      },
      {
        label: '必要权益',
        formItemKey: 'necessary_rights',
        hide: props?.detail?.structured_version < 1,
        view: true,
        span: 2,
        render: () => {
          return <EquityFormTable formItemKey='necessary_rights' view />
        }
      },
      {
        label: getExpectRightsLabel('expect_rights_diff_list'),
        formItemKey: 'expect_rights_diff_list',
        view: true,
        hide: props?.detail?.structured_version < 1,
        span: 2,
        render: () => {
          return <EquityFormTable formItemKey='expect_rights_diff_list' view />
        }
      },
      {
        label: '备注',
        formItemKey: 'remark',
        view: true,
        span: 2,
        componentClass: "textarea",
      }
    ].filter(n => n?.hide !== true)
  }, [showList])

  useEffect(() => {
    form.setFieldsValue(state)
  }, [state])

  const [form] = Form.useForm()
  const { open } = useOpenModal({
    width: 950,
    title: state.title,
    // footer: null,
    okButton: false,
    cancelButton: false,
    onOk: () => { },
    children: <div>
      <Form form={form}>
        {items.length ? <DescriptionsFormItem column={2} items={items} /> : <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
          <NoData />
          <div style={{ color: '#878A99' }}>暂无数据</div>
        </div>}
      </Form>
    </div>
  })

  const openContentDetails = useMemoizedFn(({
    title,
    property_values,
    necessary_rights,
    expect_rights_diff_list,
    kpi_values,
    cooperate_account_id,
    remark,
                                            }: {
    title: string,
    property_values: any[],
    necessary_rights: any[],
    expect_rights_diff_list: any[],
    kpi_values: any[],
    cooperate_account_id: any,
    remark: string;
  }) => {
    // if (!property_values?.length) {
    //   Modal.info({
    //     title: '暂无合作内容详情'
    //   })
    //   return
    // }
    setState({
      title,
      property_values,
      necessary_rights,
      expect_rights_diff_list,
      kpi_values,
      cooperate_account_id,
      remark,
    })
    open()
  })

  return {
    openContentDetails
  }
}

type CooperativeContentsProps = {
  Cooperative: {
    property_values?: any[]
    superior_resource_ships?: any[]
    account?: string;
    necessary_rights?: any[];
    expect_rights_diff_list?: any[];
    kpi_values?: any[];
    remark?: string;
    id?: any;
  }
  titleClassName?: string
  index?: number
  showDetailBtn?: boolean
  index_id?: number;
  tagClassName?: string;
  // 最多展示合作内容的个数
  sliceEnd?: number;
  detail?: any;
} & React.HTMLAttributes<HTMLDivElement>

// 非PO合作内容
const getPropertyValue = (Cooperative) => {
  if (!Cooperative?.property_values?.length && (Cooperative?.account || Cooperative?.superior_resource_ships?.length)) {
    return [
      {
        property_schema: {
          property_name: '合作平台',
        },
        value: Cooperative?.superior_resource_ships?.map?.(v => v.name),
      },
      {
        property_schema: {
          property_name: '需求合作内容',
        },
        value: Cooperative?.account ? [Cooperative?.account] : [],
      }
    ]
  }
  return []
}

// 合作内容
export default function CooperativeContents(props: CooperativeContentsProps) {
  const { Cooperative, titleClassName, index, index_id, className, style, showDetailBtn = true, tagClassName, sliceEnd, detail, ...restProps } = props;
  const propertyValues = Cooperative?.property_values?.length ? Cooperative?.property_values : getPropertyValue(Cooperative);
  const sliceNum = (sliceEnd && sliceEnd < propertyValues.length) ? sliceEnd - 1 : undefined

  const { openContentDetails } = useOpenContentDetails({ detail })
  return <div className={cx('mpc-cooperative-contents', className)} style={style} {...restProps}>
    <Space size={4} direction="vertical">
      <div className={cx('header')}>
        <div className={cx('title', titleClassName)}>{`合作内容${index_id || (index + 1)}`} </div>
        {showDetailBtn && <div className={cx('details-btn')} onClick={() => {
          openContentDetails({
            title: `合作内容${index_id || (index + 1)}`,
            property_values: propertyValues || [],
            necessary_rights: Cooperative.necessary_rights,
            expect_rights_diff_list: Cooperative.expect_rights_diff_list,
            kpi_values: Cooperative.kpi_values,
            remark: Cooperative.remark,
            cooperate_account_id: Cooperative.id,
          })
        }}>详情 <ChevronRight /></div>}
      </div>
      {
        propertyValues.slice(0, sliceNum).map(item => {
          const value = renderCooperationPropertyValue(item);
          return (
            <>
              <div className={cx(['title-tag', tagClassName])}>
                {renderCooperationPropertyLabel(item)}
                <span className={cx('title-details')}>{<Tooltip title={value}>{value}</Tooltip>}</span>
              </div>
            </>
          )
        })
      }
      {sliceNum && <div className={cx(['title-details', tagClassName])}>......</div>}
    </Space>
  </div>
}
